import { inject } from '@angular/core';
import { Auth, authState } from '@angular/fire/auth';
import { doc, docData, Firestore } from '@angular/fire/firestore';
import { CanMatchFn, RedirectCommand, Router } from '@angular/router';
import { catchError, map, of, switchMap, take } from 'rxjs';
import { AppPath } from '../../app.routes';
import { AuthPath } from '../../features/auth/auth.routes';
import { SignupSteps } from '../../features/auth/signup/signup.routes';
import { RegistrationState } from '../auth/registration-state';

type ProfileWithState = {
  state?: RegistrationState;
  planId?: string;
};

export const registrationProgressGuard = (): CanMatchFn => (route, segments) => {
  const auth = inject(Auth);
  const firestore = inject(Firestore);
  const router = inject(Router);

  const signupBase = ['/', AppPath.Auth, AuthPath.SignUp];
  const urlStartsWith = (step: SignupSteps) => segments.some((s) => s.path === step);

  const redirect = (step: SignupSteps, queryParams?: Record<string, string>) =>
    new RedirectCommand(
      router.createUrlTree([...signupBase, step], queryParams ? { queryParams } : undefined)
    );

  const orderedSteps: SignupSteps[] = [
    SignupSteps.Credentials,
    SignupSteps.ContactInfo,
    SignupSteps.PlanSelection,
    SignupSteps.Payment,
  ];

  return authState(auth).pipe(
    take(1),
    switchMap((user) => {
      if (!user) {
        return of<{ profile: ProfileWithState | null; hasUser: boolean }>({
          profile: null,
          hasUser: false,
        });
      }

      const profileRef = doc(firestore, 'profiles', user.uid);
      return docData<ProfileWithState>(profileRef).pipe(
        take(1),
        map((profile) => ({ profile, hasUser: true }))
      );
    }),
    take(1),
    map(({ profile, hasUser }) => {
      const state = profile?.state;
      const planId = profile?.planId;

      if (!hasUser) {
        return true;
      }

      if (!state) {
        return redirect(SignupSteps.Credentials);
      }

      const targetByState: Partial<Record<RegistrationState, SignupSteps>> = {
        'credentials-set': SignupSteps.ContactInfo,
        'contact-info-set': SignupSteps.PlanSelection,
        'plan-selected': SignupSteps.Payment,
        'payment-step': SignupSteps.Payment,
      };

      if (state === 'signup-completed') {
        return true;
      }

      const targetStep = targetByState[state as RegistrationState] ?? SignupSteps.Credentials;
      const requestedStep =
        orderedSteps.find((step) => urlStartsWith(step)) ?? SignupSteps.Credentials;

      const requestedPath = segments.some((s) => s.path === requestedStep) ? requestedStep : null;

      // Un usuario que no ha completado el registro está tratando de acceder a una ruta protegida
      // que no pertenece al flujo de registro (como /account).
      if (!requestedPath) {
        const extras = planId ? { planId } : undefined;
        return redirect(targetStep, extras);
      }

      const targetIndex = orderedSteps.indexOf(targetStep);
      const requestedIndex = orderedSteps.indexOf(requestedStep);

      // Allow going back to any previous/completed step.
      if (requestedIndex <= targetIndex) {
        return true;
      }

      const extras = planId ? { planId } : undefined;
      return redirect(targetStep, extras);
    }),
    catchError(() => of(redirect(SignupSteps.Credentials)))
  );
};
